import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import moment from "moment"
import { ArrowLeft } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { capitalize, formatCurrency } from "~/utils/helpers"
import { apiWorkspacePayments } from "~/routes"
import type Payment from "~/types/Payment"
import type PaymentItem from "~/types/PaymentItem"

const niceDate = (d?: string | null) =>
    d ? moment(d).format("MMM Do YYYY, HH:mm") : "—"

const statusBadge = (status: string, isOverdue: boolean) => {
    if (status === "paid") return <span className="badge badge-lg bg-success">Paid</span>
    if (status === "pending") {
        return (
            <span className={`badge badge-lg ${isOverdue ? "bg-danger" : "bg-warning"}`}>
        {isOverdue ? "Overdue" : "Pending"}
      </span>
        )
    }
    if (status === "overdue")
        return <span className="badge badge-lg bg-danger">Overdue</span>

    return <span className="badge badge-lg bg-secondary">Draft</span>
}

const renderDetail = (title: string, content: React.ReactNode, size = 6) => (
    <div className={`col-${size}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
            <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
    </div>
)

const PaymentView: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    const endpoint = apiWorkspacePayments.show.path({ venueSlug, id: id! })
    const {
        data: payment,
        isLoading,
    } = useFetchResource<{ payment: Payment }, "payment">(endpoint, "payment", {})

    if (isLoading || !payment) {
        return <LoadingWrapper isLoading />
    }

    const shortId = (payment.id ?? "").slice(0, 6).toUpperCase()
    const items = payment.items ?? []

    const itemsColumns = [
        {
            key: "description",
            header: "Item Description",
        },
        {
            key: "amountCents",
            header: "Unit Price",
            formatter: (amountCents: number, row: PaymentItem) =>
                formatCurrency({ cents: amountCents, currency_iso: row.currencyIso }),
        },
        {
            key: "quantity",
            header: "Quantity",
        },
        {
            key: "total",
            header: "Total",
            formatter: (_: unknown, row: PaymentItem) =>
                formatCurrency({
                    cents: (row.amountCents || 0) * (row.quantity || 0),
                    currency_iso: row.currencyIso,
                }),
        },
    ]

    return (
        <LoadingWrapper isLoading={false}>
            <PageTitle
                title={
                    <div className="d-flex align-items-center">
                        <button
                            type="button"
                            className="btn btn-link p-0 me-2"
                            onClick={() => navigate("/payments")}
                            aria-label="Back to Payments"
                            title="Back to Payments"
                        >
                            <ArrowLeft />
                        </button>
                        <span>{`Payment ${shortId ? `INV-${shortId}` : ""}`}</span>
                    </div>
                }
            />

            <div className="row">
                <div className="col-8">
                    <CatchCard title="Payment Details">
                        <div className="row">
                            {renderDetail(
                                "Status",
                                statusBadge(payment.status, payment.isOverdue),
                                6
                            )}

                            {renderDetail("Payment Method", capitalize(payment.payment_method) || "—", 6)}
                            {renderDetail("Source", payment.type || "—", 6)}

                            {renderDetail("Sent On", niceDate(payment.sentOn), 6)}
                            {renderDetail("Paid On", niceDate(payment.paidOn), 6)}

                        </div>
                    </CatchCard>

                    <CatchCard title="Items">
                        <CatchTable
                            data={items}
                            emptyMessage="No Items"
                            columns={itemsColumns}
                            footerData={{
                                className: "table table-centered",
                                rowData: [
                                    { cellData: [{ content: "" }] },
                                    {
                                        className: "bg-block",
                                        cellData: [
                                            { content: "Total", colSpan: 3 },
                                            {
                                                content: formatCurrency({
                                                    cents: payment.amount?.cents || 0,
                                                    currency_iso: payment.amount?.currency_iso || "USD",
                                                }),
                                                colSpan: 2,
                                            },
                                        ],
                                    },
                                ],
                            }}
                        />
                    </CatchCard>
                </div>
                <div className="col-4">
                    <CatchCard title="Customer">
                        <div className="row">
                            {renderDetail("Name", payment.customer?.name || "—", 12)}
                            {renderDetail("Email", payment.customer?.email || "—", 12)}
                            {renderDetail("Phone", payment.customer?.phone || "—", 12)}
                        </div>
                    </CatchCard>

                    <CatchCard title="Created By">
                        <div className="row">
                            {renderDetail("Name", payment.createdBy?.name || "—", 12)}
                            {renderDetail("Email", payment.createdBy?.email || "—", 12)}
                        </div>
                    </CatchCard>
                </div>
            </div>
        </LoadingWrapper>
    )
}

export default PaymentView
